import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import useToggle from 'react-use/lib/useToggle';
import { DraggableCore } from 'react-draggable';

import Icon from 'components/Kizen/Icon';
import {
  updateColumnWidth,
  setPageConfig,
} from 'store/customObjectsRecordsPage/actions';
import {
  EmptyCell,
  SizedCell as KizenSizedCell,
  SortableHeadCell as KizenSortableHeadCell,
} from 'components/Kizen/Table';
import {
  ResizerLayout,
  PADDING,
  getSizing,
  getColumnWidthFromConfig,
} from 'pages/Common/components/BigTableCells';
import { RECORD_LIST_CONFIG_KEYS } from 'services/TeamMemberService';
import { getFieldsDescription } from 'components/Fields/FieldInput/helpers';

import { FIELD_TYPES } from 'utility/constants';

const minResizableWidth = {
  [FIELD_TYPES.DateTime.type]: 170,
  [FIELD_TYPES.PhoneNumber.type]: 202,
  default: 75,
};

export const SizedCell = styled(({ column, minWidth, ...others }) => {
  // We pull column widths from redux not just because it is persistent/stateful,
  // but also because it greatly improves the performance versus managing this state
  // and piping it down from the TabularPage, since the whole table may re-render
  // rather than just the specific columns that were resized.
  const columnSettings = useSelector(
    (s) => s.recordsPage.pageConfig.columns[column.id]
  );

  return (
    <KizenSizedCell
      column={column}
      data-qa={column.label}
      data-qa-disabled={!column.meta.editable}
      padding={`${PADDING}px`}
      {...others}
      {...getSizing(columnSettings && columnSettings.width, minWidth)}
    />
  );
})`
  > div {
    // Fixes small alignment issue with cell text
    line-height: 0;
  }
`;

export const SortableHeadCell = styled(
  ({ column, minWidth, children, className, ...others }) => {
    const dispatch = useDispatch();
    const width = useSelector((s) =>
      getColumnWidthFromConfig(s.recordsPage, column.id)
    );

    const [resizing, toggleResizing] = useToggle(false);
    const handleResize = useCallback(
      (ev, { x }) => {
        const hasAdornments = column.bodyCell?.adornments?.length;

        const minWidth =
          (minResizableWidth[column.bodyCell?.field?.fieldType] ??
            minResizableWidth.default) + (hasAdornments ? 25 : 0);

        const nextWidth = Math.max(minWidth, x - PADDING / 2);

        dispatch(
          // !important - set or reset all properties in object
          updateColumnWidth({
            id: column.id,
            label: column.label,
            width: `${nextWidth}px`,
            visible: true,
          })
        );
      },
      [dispatch, column.id, column.label, column.bodyCell]
    );

    const handleUpdateColumnWidth = useCallback(() => {
      dispatch(
        setPageConfig({ updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.COLUMNS })
      );
    }, [dispatch]);

    return (
      <KizenSortableHeadCell
        className={`${className} ${resizing && 'Resizing'}`}
        column={column}
        padding={`${PADDING}px`}
        {...others}
        {...getSizing(width, minWidth)}
      >
        {children}
        <ResizerLayout className="ResizerLayout">
          <DraggableCore
            onDrag={handleResize}
            onStart={toggleResizing}
            onStop={() => {
              toggleResizing();
              handleUpdateColumnWidth();
            }}
          >
            <Icon className="Resizer" icon="column-resize" size="intrinsic" />
          </DraggableCore>
        </ResizerLayout>
      </KizenSortableHeadCell>
    );
  }
)`
  &.Resizing {
    cursor: ew-resize;
    svg line {
      stroke: var(--color-background-darker);
    }
  }
  > div {
    // Fixes small alignment issue with cell text
    line-height: 0;
    margin-top: 1px;
    button {
      margin-left: 10px;
    }
  }
`;

export const CustomFieldHeadCell = ({
  field = null,
  column = null,
  ...others
}) => {
  if (!field) {
    return <EmptyCell {...others} />;
  }
  const { withNameDescription } = getFieldsDescription(field);
  return (
    <SortableHeadCell
      by={field.id}
      column={column}
      {...others}
      description={withNameDescription}
    />
  );
};
